import React, { useCallback, useEffect, useState } from "react";
import AdminSectionHeader from "@/components/admin/AdminSectionHeader";
import AdminTable from "@/components/admin/AdminTable";
import { StatusBadge } from "@/components/app/StatusBadge";
import CTAButton from "@/components/app/CTAButton";
import AppTabs from "@/components/app/AppTabs";
import AppModal from "@/components/app/AppModal";
import { api, formatApiError } from "@/lib/api";
import { Boxes, Download, RefreshCw, Eye } from "lucide-react";

const TABS = [
    { key: "blocks", label: "Blocs" },
    { key: "sets", label: "Sets" },
    { key: "cards", label: "Cartes" },
    { key: "rarities", label: "Raretés" },
];

export default function AdminCatalogue() {
    const [tab, setTab] = useState("blocks");
    const [error, setError] = useState("");
    const [syncing, setSyncing] = useState(false);
    const [blocks, setBlocks] = useState([]);
    const [sets, setSets] = useState([]);
    const [cards, setCards] = useState([]);
    const [rarities, setRarities] = useState([]);
    const [previewBlockId, setPreviewBlockId] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [importing, setImporting] = useState({});
    const [activeSetId, setActiveSetId] = useState(null);

    const loadBlocks = useCallback(async () => {
        try { const { data } = await api.get("/admin/catalog/blocks", { params: { tcg_slug: "pokemon" } }); setBlocks(data); }
        catch (e) { setError(formatApiError(e)); }
    }, []);
    const loadSets = useCallback(async () => {
        try { const { data } = await api.get("/admin/catalog/sets"); setSets(data); }
        catch (e) { setError(formatApiError(e)); }
    }, []);
    const loadRarities = useCallback(async () => {
        try { const { data } = await api.get("/admin/catalog/rarities", { params: { tcg_slug: "pokemon" } }); setRarities(data); }
        catch (e) { setError(formatApiError(e)); }
    }, []);
    const loadCards = useCallback(async (setId) => {
        if (!setId) { setCards([]); return; }
        try { const { data } = await api.get(`/admin/catalog/sets/${setId}/cards`, { params: { limit: 500 } }); setCards(data); }
        catch (e) { setError(formatApiError(e)); }
    }, []);

    useEffect(() => { loadBlocks(); loadSets(); loadRarities(); }, [loadBlocks, loadSets, loadRarities]);
    useEffect(() => { if (tab === "cards" && activeSetId) loadCards(activeSetId); }, [tab, activeSetId, loadCards]);

    async function syncBlocks() {
        setSyncing(true); setError("");
        try {
            await api.post("/admin/catalog/tcgs/pokemon/sync-blocks");
            await loadBlocks();
        } catch (e) { setError(formatApiError(e)); }
        finally { setSyncing(false); }
    }

    async function toggleBlockActive(b) {
        try {
            await api.patch(`/admin/catalog/blocks/${b._id}`, { is_active: !b.is_active });
            await loadBlocks();
        } catch (e) { setError(formatApiError(e)); }
    }

    async function openPreview(block) {
        setPreviewBlockId(block._id); setPreviewData(null); setError("");
        try {
            const { data } = await api.get(`/admin/catalog/blocks/${block._id}/tcgdex-sets`);
            setPreviewData(data);
        } catch (e) { setError(formatApiError(e)); }
    }

    async function importSet(extId) {
        if (!previewBlockId) return;
        setImporting((i) => ({ ...i, [extId]: true }));
        try {
            await api.post(`/admin/catalog/blocks/${previewBlockId}/sets/${extId}/import`);
            // refresh preview
            const { data } = await api.get(`/admin/catalog/blocks/${previewBlockId}/tcgdex-sets`);
            setPreviewData(data);
            await loadSets();
            await loadRarities();
        } catch (e) { setError(formatApiError(e)); }
        finally { setImporting((i) => ({ ...i, [extId]: false })); }
    }

    async function toggleSetActive(s) {
        try {
            await api.patch(`/admin/catalog/sets/${s._id}`, { is_active: !s.is_active });
            await loadSets();
        } catch (e) { setError(formatApiError(e)); }
    }

    return (
        <>
            <AdminSectionHeader
                title="Catalogue"
                description="Architecture multi-TCG. Pokémon est une couche configuration — pas le moteur."
                action={
                    <div className="flex gap-2">
                        <CTAButton variant="outline" size="sm" loading={syncing} onClick={syncBlocks} icon={<RefreshCw className="h-3.5 w-3.5" />} testId="catalog-sync-blocks">
                            Sync blocs TCGdex
                        </CTAButton>
                    </div>
                }
            />

            {error && <div className="mb-4 rounded-lg border border-md-danger/30 bg-md-danger/10 px-4 py-2 text-sm text-md-danger">{error}</div>}

            <AppTabs items={TABS} value={tab} onChange={setTab} />

            <div className="mt-6">
                {tab === "blocks" && (
                    <AdminTable
                        emptyLabel="Aucun bloc. Cliquez sur Sync blocs TCGdex."
                        columns={[
                            { key: "order", label: "Ordre", render: (b) => <span className="font-mono text-xs">{b.order}</span> },
                            { key: "external_id", label: "ID source", render: (b) => <code className="text-xs">{b.external_id}</code> },
                            { key: "name", label: "Nom" },
                            { key: "is_active", label: "Actif", render: (b) => <StatusBadge tone={b.is_active ? "success" : "neutral"}>{b.is_active ? "actif" : "inactif"}</StatusBadge> },
                            { key: "actions", label: "", render: (b) => (
                                <div className="flex gap-2">
                                    <CTAButton size="sm" variant="outline" icon={<Eye className="h-3 w-3" />} onClick={() => openPreview(b)} testId={`block-preview-${b.external_id}`}>Sets</CTAButton>
                                    <CTAButton size="sm" variant={b.is_active ? "ghost" : "primary"} onClick={() => toggleBlockActive(b)} testId={`block-toggle-${b.external_id}`}>
                                        {b.is_active ? "Désactiver" : "Activer"}
                                    </CTAButton>
                                </div>
                            )},
                        ]}
                        rows={blocks}
                    />
                )}

                {tab === "sets" && (
                    <AdminTable
                        emptyLabel="Aucun set importé. Ouvrez un bloc et importez ses sets."
                        columns={[
                            { key: "external_id", label: "ID", render: (s) => <code className="text-xs">{s.external_id}</code> },
                            { key: "name", label: "Nom" },
                            { key: "card_count_total", label: "Cartes", render: (s) => <span className="font-mono text-xs">{s.card_count_total || 0}</span> },
                            { key: "is_active", label: "Actif", render: (s) => <StatusBadge tone={s.is_active ? "success" : "neutral"}>{s.is_active ? "actif" : "inactif"}</StatusBadge> },
                            { key: "is_imported", label: "Import", render: (s) => <StatusBadge tone={s.is_imported ? "gold" : "neutral"}>{s.is_imported ? "importé" : "—"}</StatusBadge> },
                            { key: "actions", label: "", render: (s) => (
                                <div className="flex gap-2">
                                    <CTAButton size="sm" variant="outline" onClick={() => { setActiveSetId(s._id); setTab("cards"); }} testId={`set-view-${s.external_id}`}>Voir cartes</CTAButton>
                                    <CTAButton size="sm" variant={s.is_active ? "ghost" : "primary"} onClick={() => toggleSetActive(s)} testId={`set-toggle-${s.external_id}`}>
                                        {s.is_active ? "Désactiver" : "Activer"}
                                    </CTAButton>
                                </div>
                            )},
                        ]}
                        rows={sets}
                    />
                )}

                {tab === "cards" && (
                    <>
                        {!activeSetId ? (
                            <div className="rounded-2xl border border-md-border bg-md-surface p-8 text-center text-sm text-md-text-2">
                                Sélectionnez un set depuis l’onglet Sets pour voir ses cartes.
                            </div>
                        ) : (
                            <AdminTable
                                emptyLabel="Aucune carte."
                                columns={[
                                    { key: "local_id", label: "N°", render: (c) => <span className="font-mono text-sm font-bold">{c.local_id}</span> },
                                    { key: "name", label: "Nom" },
                                    { key: "rarity_label", label: "Rareté source", render: (c) => c.rarity_label || "—" },
                                    { key: "variants", label: "Variantes", render: (c) => (
                                        <div className="flex flex-wrap gap-1">
                                            {(c.variants || []).map((v) => (
                                                <span key={v} className="rounded bg-md-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-md-text-2">{v}</span>
                                            ))}
                                        </div>
                                    )},
                                    { key: "is_active", label: "Actif", render: (c) => <StatusBadge tone={c.is_active ? "success" : "neutral"}>{c.is_active ? "actif" : "inactif"}</StatusBadge> },
                                ]}
                                rows={cards}
                            />
                        )}
                    </>
                )}

                {tab === "rarities" && (
                    <AdminTable
                        emptyLabel="Aucune rareté importée."
                        columns={[
                            { key: "label", label: "Label" },
                            { key: "source_label", label: "Source", render: (r) => <code className="text-xs">{r.source_label}</code> },
                            { key: "card_count", label: "Cartes", render: (r) => <span className="font-mono text-xs">{r.card_count}</span> },
                            { key: "is_active", label: "Actif", render: (r) => <StatusBadge tone={r.is_active ? "success" : "neutral"}>{r.is_active ? "actif" : "inactif"}</StatusBadge> },
                        ]}
                        rows={rarities}
                    />
                )}
            </div>

            <AppModal
                open={!!previewBlockId}
                onOpenChange={(o) => { if (!o) { setPreviewBlockId(null); setPreviewData(null); } }}
                title={previewData ? `Sets — ${previewData.block.name}` : "Chargement…"}
                subtitle="Importer un set récupère sa liste complète de cartes depuis TCGdex."
                icon={<Boxes className="h-5 w-5" />}
                variant="info"
                testId="block-preview-modal"
            >
                {previewData ? (
                    <ul className="space-y-2">
                        {previewData.sets.map((s) => (
                            <li key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-md-border bg-md-surface p-3">
                                <div className="min-w-0">
                                    <p className="font-medium text-md-text"><code className="mr-2 text-xs text-md-text-2">{s.id}</code>{s.name}</p>
                                    <p className="text-xs text-md-text-2">{s.cardCount?.total || 0} cartes</p>
                                </div>
                                {s.imported ? (
                                    <StatusBadge tone="success">Importé</StatusBadge>
                                ) : (
                                    <CTAButton
                                        size="sm"
                                        icon={<Download className="h-3 w-3" />}
                                        loading={!!importing[s.id]}
                                        onClick={() => importSet(s.id)}
                                        testId={`set-import-${s.id}`}
                                    >
                                        Importer
                                    </CTAButton>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="py-8 text-center text-sm text-md-text-2">Chargement…</div>
                )}
            </AppModal>
        </>
    );
}
